import type {
  RequestPasswordResetTenantRequest,
  RequestPasswordResetTenantResponse,
  SignInTenantRequest,
  SignInTenantResponse,
  SsoLinkConfirmResponse,
  SsoLinkInfoResponse,
  SsoLinkTokenRequest,
  TenantInfo,
} from "@repo/shared/types";

const BASE = `${process.env.BUN_PUBLIC_API_URL}/internal/auth-login`;

export async function getTenantBySlug(slug: string): Promise<TenantInfo | null> {
  const res = await fetch(`${BASE}/tenant-by-slug/${encodeURIComponent(slug)}`);
  if (res.status === 404) return null;
  if (!res.ok) return null;
  return res.json();
}

/**
 * Like {@link getTenantBySlug}, but only a 404 means "no such organization":
 * network and server errors throw, so callers can tell a failed lookup from a
 * missing tenant (QL-343 wrong-organization overlay).
 */
export async function lookupTenantBySlug(slug: string): Promise<TenantInfo | null> {
  const res = await fetch(`${BASE}/tenant-by-slug/${encodeURIComponent(slug)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Organization lookup failed (${res.status})`);
  return res.json();
}

export async function signInWithTenant(
  payload: SignInTenantRequest,
): Promise<SignInTenantResponse> {
  try {
    const res = await fetch(`${BASE}/sign-in`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload satisfies SignInTenantRequest),
    });
    const json = (await res.json().catch(() => null)) as SignInTenantResponse | null;
    if (json) return json;
    return { success: false };
  } catch {
    return { success: false };
  }
}

export async function requestPasswordResetWithTenant(
  payload: RequestPasswordResetTenantRequest,
): Promise<RequestPasswordResetTenantResponse> {
  try {
    const res = await fetch(`${BASE}/request-password-reset`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload satisfies RequestPasswordResetTenantRequest),
    });
    const json = (await res.json().catch(() => null)) as RequestPasswordResetTenantResponse | null;
    if (json) return json;
    return { success: false };
  } catch {
    return { success: false };
  }
}

/** Thrown by the SSO link calls; `code` is the API's stable error code, if any. */
export class SsoLinkError extends Error {
  constructor(
    public status: number,
    public code: string | undefined,
    message?: string,
  ) {
    super(message || `SSO link request failed (${status})`);
  }
}

async function postSsoLink<T>(path: string, token: string): Promise<T> {
  // Session-bound: only the signed-in user the e-mail was sent to may confirm.
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ token } satisfies SsoLinkTokenRequest),
  });
  const json = (await res.json().catch(() => null)) as
    | (T & { code?: string; error?: string })
    | null;
  if (!res.ok || !json) throw new SsoLinkError(res.status, json?.code, json?.error);
  return json;
}

/** QL-350 — what the SSO link confirmation page shows for an e-mailed token. */
export function getSsoLinkInfo(token: string): Promise<SsoLinkInfoResponse> {
  return postSsoLink<SsoLinkInfoResponse>("/sso-link/info", token);
}

/** QL-350 — confirms the link; the page then starts the SSO login again. */
export function confirmSsoLink(token: string): Promise<SsoLinkConfirmResponse> {
  return postSsoLink<SsoLinkConfirmResponse>("/sso-link/confirm", token);
}
